import { Check } from 'lucide-react';

interface Step {
  id: number;
  name: string;
  description: string;
  required: boolean;
}

interface StepNavigationProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (step: number) => void;
  canNavigate: (step: number) => boolean;
}

export default function StepNavigation({
  steps,
  currentStep,
  onStepClick,
  canNavigate
}: StepNavigationProps) {
  return (
    <nav aria-label="Progress">
      <ol className="flex items-center">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          const isClickable = canNavigate(step.id);

          return (
            <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(step.id)}
                  disabled={!isClickable}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-all ${
                    isCompleted
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : isCurrent
                        ? 'border-2 border-blue-600 bg-white'
                        : 'border-2 border-gray-300 bg-white'
                  } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5 text-white" />
                  ) : (
                    <span className={`text-sm font-medium ${
                      isCurrent ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {step.id}
                    </span>
                  )}
                </button>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.name}
                    {step.required && <span className="text-red-500 ml-0.5">*</span>}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>

              {index !== steps.length - 1 && (
                <div className="absolute top-5 left-10 -ml-px hidden w-full sm:block">
                  <div className={`h-0.5 ${isCompleted ? 'bg-blue-600' : 'bg-gray-300'}`}
                       style={{ width: 'calc(100% - 2.5rem)' }} />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
